import { useCamaraFotos } from '@/components/fotos/CamaraFotos';
import { GaleriaFotos } from '@/components/fotos/GaleriaFotos';
import { Button } from '@/components/ui/button';
import { useAutoresFotos, useColaFotos, useObrasFotos } from '@/hooks/useFotos';
import { useSubPartidas } from '@/hooks/useSubPartidas';
import type { FiltroGaleria } from '@/lib/cliente';
import { Camera, CaretLeft, CloudArrowUp } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { baseFotos } from './FotosPantalla';

/**
 * Galería de una obra: fotos agrupadas por día (más recientes primero),
 * filtros por sub-partida y autor, selección para asignar sub-partida
 * (vivienda) y botón para tomar más fotos. Incluye las fotos de las fichas.
 */
export function GaleriaObraPantalla() {
  const { obra = '' } = useParams<{ obra: string }>();
  const location = useLocation();
  const base = baseFotos(location.pathname);
  // Volver a donde se entró (lista de obras con su búsqueda); por defecto, Obras.
  const volver = (location.state as { volver?: string } | null)?.volver ?? `${base}?vista=obras`;
  const abrirCamara = useCamaraFotos();
  const cola = useColaFotos();
  const obraQ = useObrasFotos(obra);
  const info = obraQ.data?.find((o) => o.codigo === obra);
  const autoresQ = useAutoresFotos();
  const subsQ = useSubPartidas();
  const subs = useMemo(
    () =>
      (subsQ.data?.data ?? [])
        .filter((s) => s.activo)
        .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true })),
    [subsQ.data],
  );

  const [filtroSub, setFiltroSub] = useState(''); // '' | 'sin' | id
  const [autor, setAutor] = useState<number | null>(null);
  const filtro: FiltroGaleria = {
    obra,
    sub: filtroSub && filtroSub !== 'sin' ? Number(filtroSub) : null,
    sin_sub: filtroSub === 'sin',
    autor,
  };

  return (
    <div className="space-y-4 pb-24">
      <Link
        to={volver}
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <CaretLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
        Fotos
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-mono text-xl font-semibold">{obra}</h1>
          <p className="truncate text-sm text-muted-foreground">
            {info?.nombre ?? info?.area_costeo ?? ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={() => abrirCamara({ obra, etiqueta: obra })}
            disabled={info ? !info.habilitada : false}
          >
            <Camera className="h-4 w-4" weight="bold" aria-hidden="true" />
            Tomar fotos
          </Button>
          {info && !info.habilitada && (
            <span className="text-[11px] text-red-700">
              {info.motivo === 'entregada'
                ? `Entregada el ${new Date(`${info.fecha_entrega}T12:00:00`).toLocaleDateString('es-CR')}: no admite fotos nuevas`
                : 'No admite fotos nuevas'}
            </span>
          )}
        </div>
      </div>

      {cola.pendientes > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-amber-700">
          <CloudArrowUp className="h-4 w-4" weight="bold" aria-hidden="true" />
          {cola.pendientes} foto{cola.pendientes === 1 ? '' : 's'} por subir desde este teléfono
          {cola.subiendo ? ' (subiendo…)' : ''}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
          value={filtroSub}
          onChange={(e) => setFiltroSub(e.target.value)}
        >
          <option value="">Todas las sub-partidas</option>
          <option value="sin">Sin sub-partida</option>
          {info?.es_vivienda !== false &&
            subs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.codigo} {s.nombre}
              </option>
            ))}
        </select>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={autor ?? ''}
          onChange={(e) => setAutor(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Todos los autores</option>
          {(autoresQ.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.nombre}
            </option>
          ))}
        </select>
      </div>

      <GaleriaFotos
        filtro={filtro}
        base={base}
        seleccionable={!!info?.es_vivienda}
        hayFiltros={!!filtroSub || autor !== null}
      />
    </div>
  );
}
